import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import Avatar from '@mui/material/Avatar';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import type { TeamMember } from '../api/teamApi';

type MemberRowProps = {
    member: TeamMember;
    isSelf: boolean;
    onRemove: (member: TeamMember) => void;
};

function initialsFor(member: TeamMember): string {
    const first = member.firstName?.trim().charAt(0) ?? '';
    const last = member.lastName?.trim().charAt(0) ?? '';
    const initials = `${first}${last}`.toUpperCase();
    return initials.length > 0 ? initials : member.email.charAt(0).toUpperCase();
}

function displayName(member: TeamMember): string | undefined {
    const name = [member.firstName, member.lastName].filter((part) => part && part.trim().length > 0).join(' ');
    return name.length > 0 ? name : undefined;
}

export function MemberRow({ member, isSelf, onRemove }: MemberRowProps): ReactElement {
    const { t } = useTranslation();
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

    const name = displayName(member);

    const closeMenu = (): void => setAnchorEl(null);

    return (
        <Stack
            component="li"
            direction="row"
            alignItems="center"
            spacing={2}
            sx={{ py: 1.5, borderBottom: 1, borderColor: 'divider', '&:last-of-type': { borderBottom: 0 } }}>
            <Avatar>{initialsFor(member)}</Avatar>
            <Stack sx={{ flexGrow: 1, minWidth: 0 }}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                    {name ? (
                        <Typography variant="subtitle2" fontWeight={600} noWrap sx={{ minWidth: 0 }}>
                            {name}
                        </Typography>
                    ) : (
                        <Typography variant="body2" color="text.secondary" noWrap sx={{ minWidth: 0 }}>
                            {member.email}
                        </Typography>
                    )}
                    {isSelf ? (
                        <Chip
                            size="small"
                            color="primary"
                            variant="outlined"
                            label={t('SETTINGS.MEMBERS.YOU_BADGE')}
                            sx={{ flexShrink: 0 }}
                        />
                    ) : null}
                </Stack>
                {name ? (
                    <Typography variant="body2" color="text.secondary" noWrap>
                        {member.email}
                    </Typography>
                ) : null}
            </Stack>
            {isSelf ? null : (
                <>
                    <IconButton
                        aria-label={t('SETTINGS.MEMBERS.ROW_ACTIONS_ARIA', { email: member.email })}
                        onClick={(event) => setAnchorEl(event.currentTarget)}>
                        <MoreVertIcon />
                    </IconButton>
                    <Menu anchorEl={anchorEl} open={anchorEl !== null} onClose={closeMenu}>
                        <MenuItem
                            onClick={() => {
                                closeMenu();
                                onRemove(member);
                            }}>
                            <ListItemIcon>
                                <DeleteOutlineIcon fontSize="small" color="error" />
                            </ListItemIcon>
                            <Typography color="error">{t('SETTINGS.MEMBERS.ACTION_REMOVE_MEMBER')}</Typography>
                        </MenuItem>
                    </Menu>
                </>
            )}
        </Stack>
    );
}
